import { Controller } from "@hotwired/stimulus"

/**
 * Navbar Controller
 *
 * A single Stimulus controller for the entire navigation system.
 * Handles mobile hamburger toggle, desktop mega menu dropdowns, and Turbo cleanup.
 *
 * NO BOOTSTRAP DEPENDENCY - uses pure CSS classes for visibility.
 *
 * IMPORTANT: Uses event delegation instead of data-action attributes to avoid
 * duplicate binding issues with data-turbo-permanent. All click handlers are
 * attached to the document and filtered by selector.
 *
 * Fixed for Turbo Caching: No initialization flags (they get cached and break
 * on back button). Relies on connect/disconnect symmetry instead.
 *
 * @controller navbar
 */
export default class extends Controller {
  static targets = ["mobileToggle", "mobileMenu", "dropdownToggle", "dropdown"]

  initialize() {
    // Bind functions ONCE here so the reference remains stable
    // This allows removeEventListener to work correctly in disconnect
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleEscKey = this.handleEscKey.bind(this)
    this.handleTurboBeforeCache = this.handleTurboBeforeCache.bind(this)
  }

  connect() {
    // No initialization flag check - we rely on connect/disconnect symmetry.
    // If connect runs, we NEED listeners. Turbo caching would break flag checks.

    // Single document-level click handler
    document.addEventListener('click', this.handleDocumentClick, true)
    document.addEventListener('keydown', this.handleEscKey)
    document.addEventListener('turbo:before-cache', this.handleTurboBeforeCache)
  }

  disconnect() {
    // Clean up listeners
    document.removeEventListener('click', this.handleDocumentClick, true)
    document.removeEventListener('keydown', this.handleEscKey)
    document.removeEventListener('turbo:before-cache', this.handleTurboBeforeCache)
  }

  // =========================================================================
  // Central Click Handler (Event Delegation)
  // =========================================================================

  handleDocumentClick(event) {
    // If the navbar is hidden or removed from DOM, stop interacting
    if (!this.element.isConnected) return

    const target = event.target

    // 1. Mobile Toggle
    const mobileToggle = target.closest('[data-navbar-target="mobileToggle"]')
    if (mobileToggle && this.element.contains(mobileToggle)) {
      event.preventDefault()
      event.stopPropagation()
      this.toggleMobile()
      return
    }

    // 2. Dropdown Toggle
    const dropdownToggle = target.closest('[data-navbar-target="dropdownToggle"]')
    if (dropdownToggle && this.element.contains(dropdownToggle)) {
      event.preventDefault()
      event.stopPropagation()
      this.toggleDropdown(dropdownToggle)
      return
    }

    // 3. Click Outside (Close Everything)
    // We check if the click was *inside* the navbar. If not, close.
    if (!this.element.contains(target)) {
      this.closeAll()
    }
  }

  // =========================================================================
  // Mobile Menu Logic
  // =========================================================================

  toggleMobile() {
    if (!this.hasMobileMenuTarget || !this.hasMobileToggleTarget) return

    const isOpen = this.mobileMenuTarget.classList.contains('show')

    if (isOpen) {
      this.closeMobile()
    } else {
      this.closeAllDropdowns()
      this.openMobile()
    }
  }

  openMobile() {
    this.mobileMenuTarget.classList.add('show')
    this.mobileToggleTarget.setAttribute('aria-expanded', 'true')
    this.mobileToggleTarget.classList.remove('collapsed')
    document.body.classList.add('nav-mobile-open')
  }

  closeMobile() {
    // Safety check in case targets are missing during teardown
    if (!this.hasMobileMenuTarget || !this.hasMobileToggleTarget) return

    this.mobileMenuTarget.classList.remove('show')
    this.mobileToggleTarget.setAttribute('aria-expanded', 'false')
    this.mobileToggleTarget.classList.add('collapsed')
    document.body.classList.remove('nav-mobile-open')
  }

  // =========================================================================
  // Desktop Dropdowns
  // =========================================================================

  toggleDropdown(toggle) {
    const dropdownId = toggle.getAttribute('aria-controls')
    const dropdown = document.getElementById(dropdownId)

    if (!dropdown) return

    const isOpen = dropdown.classList.contains('show')

    // Close others first (Accordion behavior)
    this.closeAllDropdowns()

    if (!isOpen) {
      dropdown.classList.add('show')
      toggle.setAttribute('aria-expanded', 'true')
      toggle.classList.add('show')
    }
  }

  closeAllDropdowns() {
    this.dropdownTargets.forEach(dropdown => {
      dropdown.classList.remove('show')
    })

    this.dropdownToggleTargets.forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false')
      toggle.classList.remove('show')
    })
  }

  // =========================================================================
  // Global Event Handlers
  // =========================================================================

  handleEscKey(event) {
    if (event.key === 'Escape') {
      this.closeAll()
    }
  }

  handleTurboBeforeCache() {
    // This ensures the menu is closed in the cache,
    // preventing the "Flash of Open Menu" on back button.
    this.closeAll()
  }

  closeAll() {
    this.closeMobile()
    this.closeAllDropdowns()
  }
}
